import { useQuery } from '@tanstack/react-query';
import React, { ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { DefaultTheme, useTheme } from 'styled-components';
import { groupBy, throttle } from 'underscore';

import { GcpModel } from '../../../../../../typings/api/skymap/rest/v0/.common';
import {
  GeodataMarker,
  GeodataMarkerEstimate,
} from '../../../../../../typings/api/skymap/rest/v0/geodata';
import { SkyMapAxiosServiceFactory } from '../../../../services/axios/skymap-axios-service-factory';
import { isDefined } from '../../../../utils/variables';
import { SubscriptionTopic } from '../../../../viewer/messaging/pubsub';
import { useErrorHandling } from '../../../hooks/use-error-handling';
import { useSubscribeCallback } from '../../../hooks/use-subscribe';
import { Icon } from '../../icon/icon';
import { GeodataImageItem, GeodataImagesContext } from '../geodata-images-state';
import { GeodataContext } from '../geodata-state';
import { calculateGcpError } from './calculate-gcp-error';
import { useGetMarkersQuery } from './use-get-markers-query';

type SortedImageItem = {
  imageId: string;
  gcpId?: string;
};

export type GcpOptimizationStatus =
  | 'OPTIMIZED'
  | 'UNOPTIMIZED'
  | 'HIGH_ERROR'
  | 'LOW_COUNT'
  | 'NONE';

export type GcpWithErrors = {
  id: string;
  name: string;
  enu: number[];
  status: GcpModel['status'];
  northing: number;
  easting: number;
  height: number;
  errorX: number;
  errorY: number;
  errorZ: number;
  rmseTotal: number;
  optimizationStatus: GcpOptimizationStatus;
  pinnedFlags: number;
  isOptimized: boolean;
  errorPointcloudZ?: number;
};

export interface PlaceMarkersContext {
  image: GeodataImageItemWithMarkers | undefined;
  selectImage: (image: GeodataImageItemWithMarkers | undefined) => void;
  gcp: GcpModel | undefined;
  gcpsWithErrors: GcpWithErrors[];
  selectGcp: (value: GcpModel | undefined) => void;
  updateMarker: (marker: GeodataMarker) => void;
  deleteMarker: (markerId: string) => Promise<void>;
  showHighGcpErrorWarning: boolean;
  showLowGcpCountWarning: boolean;
  showUnoptimizedGcpWarning: boolean;
  markers: GeodataMarkerWithEstimates[];
  estimates: GeodataMarkerEstimate[];
  showPlaceMarkersDialog: boolean;
  setShowPlaceMarkersDialog: (state: boolean) => void;
  imagesWithMarkers: GeodataImageItemWithMarkers[];
  setSortedImageList: (items: SortedImageItem[]) => void;
  sortedImageList: SortedImageItem[];
  refetchMarkers: () => void;
  isPendingMarkers: boolean;
  isLoadingEstimates: boolean;
}

export const PlaceMarkersContext = React.createContext<PlaceMarkersContext>(undefined!);

type Props = {
  children: React.ReactNode;
};

/**
 * Returns a list of GCP estimates for the geodata.
 */
function useGetGcpEstimatesQuery(geodataId: string) {
  const [queryId] = useState(geodataId);
  const { buildErrorList } = useErrorHandling();

  const { data, refetch, isPending } = useQuery({
    queryKey: ['getMarkerEstimates', queryId],
    queryFn: async () => {
      const response = await SkyMapAxiosServiceFactory.instance
        .createGeodataServiceV0()
        .getMarkerEstimates({
          path: {
            geodataId,
          },
        });

      return response.data;
    },
    networkMode: 'always',
    retryOnMount: false,
    retry: false,
    refetchInterval: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 0,
  });

  /**
   * Throttle so at most one refetch request is done every 2 seconds.
   */
  const throttledRefetch = useMemo(
    () => throttle(refetch, 2000, { leading: false, trailing: true }),
    [refetch],
  );

  const estimates = useMemo(() => data ?? [], [data]);
  const errorList = useMemo(() => buildErrorList(), [buildErrorList]);

  return {
    estimates,
    refetchEstimates: throttledRefetch,
    errorList,
    isLoadingEstimates: isPending,
  };
}

type ImageMarkerStatus = 'PINNED' | 'ESTIMATED' | 'NONE';

function getImageMarkerStatus(markers: GeodataMarker[]): ImageMarkerStatus {
  return markers.some((x) => x.pinned === true)
    ? 'PINNED'
    : markers.length > 0
      ? 'ESTIMATED'
      : 'NONE';
}

export function GeodataMarkerStatusIcon(status: ImageMarkerStatus, theme: DefaultTheme): ReactNode {
  const color =
    status === 'PINNED'
      ? '#88eb24'
      : status === 'ESTIMATED'
        ? theme.color.blue
        : theme.color.gray.medium;

  return <Icon color={color} fixedWidth={true} icon={['fas', 'flag']} />;
}

export type GeodataImageItemWithMarkers = GeodataImageItem & {
  markerStatusIcon: ReactNode;
  markerStatus: ImageMarkerStatus;
  markers: GeodataMarkerWithEstimates[];
};

export type GeodataMarkerWithEstimates = GeodataMarker & {
  estimates?: GeodataMarkerEstimate[];
};

export const PlaceMarkersState = (props: Props) => {
  const { geodata, isPending } = useContext(GeodataContext);
  const { images, refetchImages } = useContext(GeodataImagesContext);
  const [showDialog, setShowDialog] = useState(false);
  const [sortedImageList, setSortedImageList] = useState<SortedImageItem[]>([]);
  const { markers, refetchMarkers, updateMarker, deleteMarker, isPendingMarkers } =
    useGetMarkersQuery(geodata.id);
  const { estimates, refetchEstimates, isLoadingEstimates } = useGetGcpEstimatesQuery(geodata.id);

  useSubscribeCallback(SubscriptionTopic.OnWebsocketReconnected, () => {
    void refetchMarkers();
    void refetchEstimates();
  });

  useSubscribeCallback(SubscriptionTopic.WebsocketMessageRecieved, (event) => {
    if (event.message === 'MarkerEstimateCreated' && event.geodataId === geodata.id) {
      void refetchEstimates();
    }
  });

  const [image, setImage] = useState<GeodataImageItemWithMarkers | undefined>();
  const [gcp, setGcp] = useState<GcpModel | undefined>();
  const theme = useTheme();

  // Refetch markers and images automatically when the process is ready for GCP again. Will trigger after an optimization for example.
  useEffect(() => {
    if (geodata.status === 'user_move_flags') {
      refetchMarkers();
      void refetchImages();
    }
  }, [geodata.status, refetchMarkers, refetchImages]);

  const markersWithEstimates = useMemo<GeodataMarkerWithEstimates[]>(() => {
    const estimatesByMarkerId = groupBy(estimates, 'markerId');
    return markers.map((marker) => ({
      ...marker,
      estimates: estimatesByMarkerId[marker.id] ?? [],
    }));
  }, [markers, estimates]);

  const imagesWithMarkers = useMemo<GeodataImageItemWithMarkers[]>(() => {
    const markersByImageId = groupBy(markersWithEstimates, 'imageId');
    return images.map((image) => {
      const imageMarkers: GeodataMarker[] =
        (isDefined(image.imageId) && markersByImageId[image.imageId]) || [];
      const markerStatus = getImageMarkerStatus(imageMarkers);

      return {
        ...image,
        markers: imageMarkers,
        markerStatus,
        markerStatusIcon: GeodataMarkerStatusIcon(markerStatus, theme),
      };
    });
  }, [images, markersWithEstimates, theme]);

  useEffect(() => {
    const points = geodata?.gcpCollection?.points ?? [];

    const matchedGcp = isDefined(gcp) ? points.find((x) => x.id === gcp.id) : undefined;

    // Update GCP only if it is not set or not found in the list.
    if (!isDefined(matchedGcp)) {
      setGcp(points.length > 0 ? points[0] : undefined);
    } else if (matchedGcp !== gcp) {
      setGcp(matchedGcp);
    }
  }, [geodata, gcp]);

  useEffect(() => {
    const matchedImage = isDefined(image)
      ? imagesWithMarkers.find((x) => x.imageId === image.imageId)
      : undefined;

    // Update Image if it is not set, not found in the list, or if the current image needs to be refreshed
    if (!isDefined(matchedImage)) {
      setImage(imagesWithMarkers.length > 0 ? imagesWithMarkers[0] : undefined);
    } else if (matchedImage !== image) {
      setImage(matchedImage);
    }
  }, [imagesWithMarkers, image]);

  // Create items using useMemo to avoid infinite loops
  const gcpsWithErrors = useMemo<GcpWithErrors[]>(() => {
    if (!isDefined(geodata.gcpCollection?.points)) {
      return [];
    }

    return geodata.gcpCollection.points
      .filter((x) => isDefined(x.id))
      .map((x) => {
        const matchingMarkers = markersWithEstimates.filter((marker) => marker.gcpId === x.id);
        const errors = calculateGcpError(x, matchingMarkers);

        const isOptimized =
          errors.optimizedCount === errors.pinnedCount && errors.optimizedCount > 0;
        const rmseTotal = errors.rmseEnu?.length() ?? 0;

        const getOptimizationStatus = (): GcpOptimizationStatus => {
          if (x.status !== 'ENABLED' || matchingMarkers.length === 0) {
            return 'NONE';
          }

          if (rmseTotal > 0.025) {
            return 'HIGH_ERROR';
          }
          if (errors.pinnedCount < 3) {
            return 'LOW_COUNT';
          }

          return isOptimized ? 'OPTIMIZED' : 'UNOPTIMIZED';
        };

        return {
          id: x.id!,
          name: x.name,
          enu: x.enu,
          status: x.status,
          easting: x.enu[0],
          northing: x.enu[1],
          height: x.enu[2],
          errorX: errors.biasEnu?.x ?? 0,
          errorY: errors.biasEnu?.y ?? 0,
          errorZ: errors.biasEnu?.z ?? 0,
          rmseTotal,
          optimizationStatus: getOptimizationStatus(),
          pinnedFlags: errors.pinnedCount,
          errorPointcloudZ: isDefined(x.groundHeight) ? x.enu[2] - x.groundHeight : undefined,
          isOptimized,
        } satisfies GcpWithErrors;
      });
  }, [geodata, markersWithEstimates]);

  // memo to see if there are any gcps with totalError exceeding 0.025
  const showHighGcpErrorWarning = useMemo(() => {
    if (isPending || isPendingMarkers) {
      return false;
    }

    return gcpsWithErrors.some((gcp) => gcp.rmseTotal > 0.025);
  }, [gcpsWithErrors, isPending, isPendingMarkers]);

  // memo to see if at least 3 pinned flags are set on 3 of the gcps.
  const showLowGcpCountWarning = useMemo(() => {
    if (isPending || isPendingMarkers || gcpsWithErrors.length < 3) {
      return false;
    }

    return (
      gcpsWithErrors.filter((gcp) => gcp.status === 'ENABLED' && gcp.pinnedFlags >= 3).length < 3
    );
  }, [gcpsWithErrors, isPending, isPendingMarkers]);

  const showUnoptimizedGcpWarning = useMemo(() => {
    if (isPending || isPendingMarkers) {
      return false;
    }

    return gcpsWithErrors.some((gcp) => gcp.status === 'ENABLED' && !gcp.isOptimized);
  }, [gcpsWithErrors, isPending, isPendingMarkers]);

  const contextValue = useMemo<PlaceMarkersContext>(() => {
    return {
      image,
      gcp,
      selectGcp: setGcp,
      selectImage: setImage,
      updateMarker,
      deleteMarker,
      gcpsWithErrors,
      showHighGcpErrorWarning,
      showLowGcpCountWarning,
      showUnoptimizedGcpWarning,
      setShowPlaceMarkersDialog: setShowDialog,
      showPlaceMarkersDialog: showDialog,
      markers: markersWithEstimates,
      estimates,
      imagesWithMarkers,
      refetchMarkers,
      sortedImageList,
      setSortedImageList,
      isPendingMarkers,
      isLoadingEstimates,
    };
  }, [
    image,
    gcp,
    setImage,
    setGcp,
    showDialog,
    setShowDialog,
    markersWithEstimates,
    estimates,
    refetchMarkers,
    imagesWithMarkers,
    updateMarker,
    deleteMarker,
    sortedImageList,
    setSortedImageList,
    showHighGcpErrorWarning,
    showLowGcpCountWarning,
    showUnoptimizedGcpWarning,
    gcpsWithErrors,
    isPendingMarkers,
    isLoadingEstimates,
  ]);

  return (
    <PlaceMarkersContext.Provider value={contextValue}>
      {props.children}
    </PlaceMarkersContext.Provider>
  );
};
